import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  B24UserOption,
  CreateEmployeeRequest,
  EmployeeSecretResult,
  EmployeeSummary,
  Role,
} from '@blesk/contracts';
import { apiGet, apiPost } from '../../shared/api';
import { ROLE_LABEL } from '../../shared/labels';
import { useSession } from '../../shared/session';
import { Dialog, ErrorNote, SecretValue } from './ui';

/**
 * §13, §15.1, §15.3. Заведение сотрудника и одноразовый показ пароля.
 *
 * Диалог живёт не в «Пользователях и ролях», а здесь, потому что нужен и в
 * «Объектах»: менеджера объекта выбирают при создании, и если владельца проекта
 * ещё нет среди сотрудников, заводить его нужно там же. Уходить за этим в другой
 * раздел значит бросить наполовину заполненную форму объекта.
 */
/**
 * §15.1, §15.3. Учётную запись заводит сисадмин; способов войти два, и хотя бы
 * один обязан быть: пароль либо пользователь портала. Пароль не набирается
 * руками — сервер генерирует его со всеми четырьмя классами символов и
 * показывает один раз (§18), как PIN приглашения.
 */
export function EmployeeCreateDialog({
  onClose,
  onSaved,
  onPassword,
  prefill,
  onCreated,
}: {
  onClose: () => void;
  onSaved: () => void;
  onPassword: (issued: { employee: string; password: string }) => void;
  /** Приходит из «Объектов»: владелец проекта портала уже известен по имени и ID */
  prefill?: { fullName?: string; bitrixUserId?: number; role?: Role };
  /** Чтобы вызвавший экран сразу подставил заведённого сотрудника в своё поле */
  onCreated?: (employee: EmployeeSummary) => void;
}) {
  const session = useSession();
  const integrated = session.mode !== 'STANDALONE';
  const embedded = session.mode === 'EMBEDDED_INTEGRATED';

  const [fullName, setFullName] = useState(prefill?.fullName ?? '');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>(prefill?.role ?? 'PROJECT_MANAGER');
  const [bitrixUserId, setBitrixUserId] = useState(
    prefill?.bitrixUserId ? String(prefill.bitrixUserId) : '',
  );
  // Во встроенном режиме вход идёт через портал, и пароль по умолчанию не нужен
  const [localLogin, setLocalLogin] = useState(!embedded);

  const portalUsers = usePortalUsers(integrated);

  const save = useMutation({
    mutationFn: (body: CreateEmployeeRequest) =>
      apiPost<EmployeeSecretResult>('/api/employees', body),
    onSuccess: (result) => {
      onSaved();
      onCreated?.(result.employee);
      onClose();
      if (result.password) {
        onPassword({ employee: result.employee.fullName, password: result.password });
      }
    },
  });

  /** Выбор пользователя портала подставляет то, что портал уже знает о человеке. */
  const pickPortalUser = (value: string) => {
    setBitrixUserId(value);
    const user = (portalUsers.data ?? []).find((option) => String(option.bitrixUserId) === value);
    if (!user) return;
    if (!fullName) setFullName(user.fullName);
    if (!email && user.email) setEmail(user.email);
  };

  return (
    <Dialog title="Новый сотрудник" onClose={onClose}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            fullName,
            email,
            role,
            ...(bitrixUserId ? { bitrixUserId: Number(bitrixUserId) } : {}),
            localLogin,
          });
        }}
      >
        {integrated && (
          <label>
            Пользователь Bitrix24
            <select value={bitrixUserId} onChange={(event) => pickPortalUser(event.target.value)}>
              <option value="">— не сопоставлен —</option>
              {(portalUsers.data ?? []).map((option) => (
                <option
                  key={option.bitrixUserId}
                  value={option.bitrixUserId}
                  disabled={option.linkedEmployeeId !== null}
                >
                  {option.fullName}
                  {option.position ? ` · ${option.position}` : ''}
                  {option.linkedEmployeeId ? ' · уже заведён' : ''}
                </option>
              ))}
            </select>
            {/* §11.2 — основание, но в подсказке параграфам не место: читает заказчик */}
            <span className="muted">
              Пока сотрудник не сопоставлен с пользователем портала, назначать его исполнителем
              задач Bitrix24 нельзя.
            </span>
          </label>
        )}
        <label>
          ФИО
          <input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
            minLength={3}
          />
        </label>
        <label>
          Почта
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
        </label>
        <label>
          Роль
          <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
            {Object.entries(ROLE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {embedded ? (
          <label className="check">
            <input
              type="checkbox"
              checked={localLogin}
              onChange={(event) => setLocalLogin(event.target.checked)}
            />
            <span>Нужен вход по паролю</span>
            <span className="muted">
              Во встроенном режиме сотрудник входит из портала. Пароль нужен только тем, кто
              открывает админку по прямому адресу.
            </span>
          </label>
        ) : (
          <p className="muted">
            Пароль сгенерируем и покажем один раз: от 16 символов, строчные и прописные буквы,
            цифры и знаки. Смена при первом входе обязательна.
          </p>
        )}

        <ErrorNote error={save.error ?? portalUsers.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Создать
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * §16.2: пользователи портала читаются `user.get`. Запрос уходит только в
 * интегрированных режимах — в автономном отвечать на него нечем.
 */
function usePortalUsers(enabled: boolean) {
  return useQuery({
    queryKey: ['employees', 'bitrix-options'],
    queryFn: () => apiGet<B24UserOption[]>('/api/employees/bitrix/options'),
    enabled,
  });
}

/**
 * §18: пароль показывается ровно один раз. В базе он лежит хешем, повторно
 * получить его нельзя — только сбросить и выпустить новый.
 */
export function PasswordDialog({
  employee,
  password,
  onClose,
}: {
  employee: string;
  password: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      title={`Пароль · ${employee}`}
      onClose={onClose}
      confirmClose="Пароль сохранён? Повторно он не показывается."
    >
      <p className="error">
        Пароль показывается один раз. После закрытия окна получить его снова нельзя — только
        сбросить и выпустить новый.
      </p>
      <SecretValue
        title="Пароль"
        value={password}
        ariaLabel="Пароль сотрудника"
        buttonLabel="Скопировать пароль"
      />
      <p className="muted">
        Передайте сотруднику лично: смена при первом входе обязательна (§15.3).
      </p>
      <div className="form__actions">
        <button type="button" className="btn-primary" onClick={onClose}>
          Готово, пароль сохранён
        </button>
      </div>
    </Dialog>
  );
}

